import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CustomAutoComplete from "./CustomAutoComplete";
import { MuiDateRangePicker } from "../../Components/DateRangePicker";
import { DateRangeIcon, ClearIcon } from "@mui/x-date-pickers";
import { showSnack } from "../../store/slices/constants";
import { useAppDispatch } from "../../hooks/storeHooks";
import {
  fetchAllGoals,
  fetchAllSections,
} from "../../store/slices/contentFeedback/contentFeedbackSlice";

type FilterPopupProps = {
  masters: any;
  staffList: any[];
  filters: any;
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  onPageChange: (page: number) => void;
  dFilter: any;
  setSearch: (val: string) => void;
  setLoading: (val: boolean) => void;
  serach: string;
  handleInputChange: any;
  date?: string;
};

const FilterPopup: React.FC<FilterPopupProps> = ({
  masters,
  staffList,
  filters,
  setFilters,
  onPageChange,
  dFilter,
  setSearch,
  setLoading,
  serach,
  handleInputChange,
  date,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  // NEW: Local filter state (work in progress until Apply)
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync localFilters when popup opens
  React.useEffect(() => {
    if (anchorEl) setLocalFilters(filters);
  }, [anchorEl, filters]);
  const dispatch = useAppDispatch();
  const [openDate, setOpen] = useState(false);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const formatDateRange = (range: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    const formatDate = (date: Date | null) => {
      if (!date) return "";
      if (date && date.getDate) {
        const day = date.getDate();
        const month = date.toLocaleString("default", { month: "2-digit" }); // Get the full month name
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    };

    const start = range.startDate ? formatDate(new Date(range.startDate)) : "";
    const end = range.endDate ? formatDate(new Date(range.endDate)) : "";
    return start && end ? `${start} - ${end}` : "";
  };

  const [dateDialog, setOpenDateDialog] = useState(false);
  const handleDateRangeChange = ({ startDate, endDate }: any) => {
    if (startDate && endDate) {
      setLocalFilters((prev: any) => ({
        ...prev,
        duration: "Custom",
        duration_id: "custom",
        start_date: startDate,
        end_date: endDate,
      }));
    } else {
      dispatch(
        showSnack({
          message: "Please select the range.",
          severity: "error",
          open: true,
        })
      );
    }
    setOpen(false);
  };

  const toggle = () => setOpen(!open);

  // compare objects shallowly
  const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

  const isApplyDisabled = isEqual(localFilters, filters);
  const isClearDisabled = isEqual(
    filters,
    masters.exams && masters.exams.length === 1
      ? {
          ...dFilter,
          exam: masters.exams[0].name,
          exam_id: masters.exams[0].id,
        }
      : dFilter
  );

  useEffect(() => {
    dispatch(fetchAllGoals(localFilters.exam_id ? localFilters.exam_id : ""));
  }, [dispatch, localFilters.exam_id]);
  useEffect(() => {
    dispatch(
      fetchAllSections(localFilters.goal_id ? localFilters.goal_id : "")
    );
  }, [dispatch, localFilters.goal_id]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const commonSx = {
    width: "100%",
    "& .MuiInputBase-root": {
      padding: "5px 7px!important",
      borderRadius: "4px",
    },
    "& .MuiOutlinedInput-input": { padding: "0 !important" },
  };
  const chipData = [
    filters.exam && { key: "exam", label: `Exam: ${filters.exam}` },
    filters.goal && { key: "goal", label: `Goal: ${filters.goal}` },
    filters.reported_by && {
      key: "reported_by",
      label: `Reported By: ${filters.reported_by}`,
    },
    filters.reviewed_by && {
      key: "reviewed_by",
      label: `Reviewed By: ${filters.reviewed_by}`,
    },
    filters.section && {
      key: "section",
      label: `Section: ${filters.section}`,
    },
    filters.duration && {
      key: "duration",
      label: `Period: ${
        filters.duration === "Custom" ? date : filters.duration
      }`,
    },
  ].filter(Boolean);

  const handleDeleteChip = (key: string) => {
    const da =
      key === "exam"
        ? {
            exam: "",
            exam_id: "",
            goal: "",
            goal_id: "",
            section: "",
            section_id: "",
          }
        : key === "goal"
        ? { goal: "", goal_id: "", section: "", section_id: "" }
        : key === "section"
        ? {
            section: "",
            section_id: "",
          }
        : {};
    setFilters((prev: any) => ({
      ...prev,
      ...da,
      [key]: "",
      [`${key}_id`]: "",
    }));
    onPageChange(1);
  };
  return (
    <>
      {/* Filter Button */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        justifyContent={"flex-end"}
      >
        {chipData.map((chip: any, i: number) => (
          <Chip
            key={i}
            label={chip.label}
            onDelete={
              i === 0 && masters.exams.length === 1
                ? undefined
                : () => handleDeleteChip(chip.key)
            }
            variant="outlined"
            color="primary"
          />
        ))}
        <Box sx={{ cursor: "pointer" }} onClick={handleOpen}>
          <FilterAltIcon />
        </Box>
      </Box>
      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: "570px !important",
            maxWidth: "570px !important",
            boxSizing: "border-box",
          },
        }}
      >
        <Box fontWeight="bold" fontSize="1.1rem">
          Filter By
        </Box>
        <Divider sx={{ m: "15px 0" }} />
        <Grid container spacing={2}>
          {/* Exams */}
          {masters.exams?.length > 0 && (
            <>
              <Grid item xs={5}>
                <Box fontWeight="500">Exams</Box>
              </Grid>
              <Grid item xs={7}>
                <CustomAutoComplete
                  sx={commonSx}
                  placeholder="All Exams"
                  listLabel="name"
                  disabled={masters.exams.length === 1}
                  list={masters.exams}
                  value={masters.exams.find(
                    (s: { name: any }) => s.name === (localFilters.exam ?? "")
                  )}
                  onChange={(val: any) => {
                    setLocalFilters((prev: any) =>
                      val?.name
                        ? {
                            ...prev,
                            exam: val?.name || "",
                            exam_id: val?.id || "",
                            goal: "",
                            goal_id: "",
                            section: "",
                            section_id: "",
                          }
                        : {
                            ...prev,
                            exam: "",
                            exam_id: "",
                            goal: "",
                            goal_id: "",
                            section: "",
                            section_id: "",
                          }
                    );
                  }}
                  label={""}
                  error={false}
                  helperText={""}
                />
              </Grid>
            </>
          )}

          {/* Goals */}
          {masters.goals?.length > 0 && (
            <>
              <Grid item xs={5}>
                <Box fontWeight="500">Goals</Box>
              </Grid>
              <Grid item xs={7}>
                <CustomAutoComplete
                  sx={commonSx}
                  listLabel="name"
                  placeholder="All Goals"
                  list={masters.goals}
                  value={masters.goals.find(
                    (s: { name: any }) => s.name === (localFilters.goal ?? "")
                  )}
                  onChange={(val: any) => {
                    setLocalFilters((prev: any) =>
                      val?.name
                        ? {
                            ...prev,
                            goal: val?.name || "",
                            goal_id: val?.id || "",
                            section: "",
                            section_id: "",
                          }
                        : {
                            ...prev,
                            goal: "",
                            goal_id: "",
                            section: "",
                            section_id: "",
                          }
                    );
                  }}
                  label={""}
                  error={false}
                  helperText={""}
                />
              </Grid>
            </>
          )}

          {/* Sections */}
          {masters.sections?.length > 0 && (
            <>
              <Grid item xs={5}>
                <Box fontWeight="500">Sections</Box>
              </Grid>
              <Grid item xs={7}>
                <CustomAutoComplete
                  sx={commonSx}
                  fieldLabel=""
                  placeholder="All Sections"
                  listLabel="name"
                  list={masters.sections}
                  value={masters.sections.find(
                    (s: { name: any }) =>
                      s.name === (localFilters.section ?? "")
                  )}
                  onChange={(val: any) => {
                    setLocalFilters((prev: any) => ({
                      ...prev,
                      section: val ? val.name : "",
                      section_id: val ? val.id : "",
                    }));
                  }}
                  error={false}
                  helperText={""}
                  label={""}
                />
              </Grid>
            </>
          )}

          {/* Reported By */}
          <Grid item xs={5}>
            <Box fontWeight="500">Reported By</Box>
          </Grid>
          <Grid item xs={7}>
            <CustomAutoComplete
              input={serach ?? ""}
              onInputChange={handleInputChange}
              sx={commonSx}
              listLabel="full_name_id"
              noOptionsText="no data found yet."
              list={masters.reportedBy || []}
              placeholder="Type to search by name, id or phone..."
              value={(masters.reportedBy ?? []).find(
                (s: { full_name_id: any }) =>
                  s.full_name_id === (localFilters.reported_by ?? "")
              )}
              onChange={(val: any) => {
                setSearch("");
                setLocalFilters((prev: any) => ({
                  ...prev,
                  reported_by: val?.full_name_id || "",
                  reported_by_id: val?.id || "",
                }));
              }}
              label={""}
              error={false}
              helperText={""}
            />
          </Grid>

          {/* Reviewed By */}
          {staffList?.length > 0 && (
            <>
              <Grid item xs={5}>
                <Box fontWeight="500">Reviewed By</Box>
              </Grid>
              <Grid item xs={7}>
                <CustomAutoComplete
                  sx={commonSx}
                  listLabel="full_name"
                  placeholder="Reviewed by all"
                  list={staffList}
                  value={staffList.find(
                    (s: { full_name: any }) =>
                      s.full_name === (localFilters.reviewed_by ?? "")
                  )}
                  onChange={(val: any) => {
                    setLocalFilters((prev: any) => ({
                      ...prev,
                      reviewed_by: val ? val.full_name : "",
                      reviewed_by_id: val ? val.id : "",
                    }));
                  }}
                  error={false}
                  helperText={""}
                  label={""}
                />
              </Grid>
            </>
          )}

          {/* Period */}
          <Grid item xs={5}>
            <Box fontWeight="500">Period</Box>
          </Grid>
          <Grid item xs={7}>
            <CustomAutoComplete
              sx={commonSx}
              listLabel="name"
              placeholder="All time"
              list={masters.durations ?? ""}
              value={masters.durations.find(
                (s: { name: any }) => s.name === (localFilters.duration ?? "")
              )}
              onChange={(val: any) => {
                if (val.name === "Custom") {
                  setOpenDateDialog(true);
                  setOpen(true);
                } else {
                  if (dateDialog || openDate) {
                    setOpenDateDialog(false);
                    setOpen(false);
                  }
                  setLocalFilters((prev: any) => ({
                    ...prev,
                    duration: val.name ?? "",
                    duration_id: val.id ?? "",
                  }));
                }
              }}
              error={false}
              helperText={""}
              label={""}
            />
          </Grid>
          {dateDialog && (
            <Grid item xs={5}>
              <Box fontWeight="500">Custom Date Range</Box>
            </Grid>
          )}
          {dateDialog && (
            <Grid item xs={7}>
              <TextField
                label="Select Date"
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                onClick={(e) => {
                  setOpen(!openDate); // This will toggle the open state
                }}
                value={
                  localFilters.start_date && localFilters.end_date
                    ? formatDateRange({
                        startDate: localFilters.start_date,
                        endDate: localFilters.end_date,
                      })
                    : ""
                }
                sx={{ width: "100%" }}
                style={{ cursor: "pointer" }}
                size={"medium"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <DateRangeIcon />
                      {localFilters.start_date && localFilters.end_date && (
                        <IconButton
                          onClick={(e: any) => {
                            e.stopPropagation(); // Prevent opening the picker when clicking the clear button
                            handleDateRangeChange([null, null]); // Clear the date range
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <MuiDateRangePicker
                open={openDate}
                toggle={toggle}
                dateRange={{
                  startDate: localFilters.start_date,
                  endDate: localFilters.end_date,
                }}
                onDateRangeChange={handleDateRangeChange}
              />
            </Grid>
          )}
        </Grid>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="outlined"
            size="small"
            style={{ textTransform: "none", marginRight: "8px" }}
            disabled={isClearDisabled}
            onClick={() => {
              if (masters.exams && masters.exams.length === 1) {
                setFilters({
                  ...dFilter,
                  exam: masters.exams[0].name,
                  exam_id: masters.exams[0].id,
                });
              } else {
                setFilters({ ...dFilter });
                // here clear all list
              }
              onPageChange(1);
              setSearch("");
              setLoading(true);
              handleClose();
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            style={{ textTransform: "none" }}
            disabled={isApplyDisabled}
            onClick={() => {
              setFilters(localFilters); // ✅ Save changes
              onPageChange(1);
              setLoading(true);
              handleClose();
            }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default FilterPopup;
